"use client";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useContext, useMemo } from "react";
import makeStyles from "../../theming/make-styles.js";
import Checkbox from "../checkbox/index.js";
import getColorComponent from "../utils/get-color-component.js";
import TableCell from "./table-cell.js";
import { TableContext, TableSectionContext } from "./table.js";
const useStyles = makeStyles()((theme, { borders, color, hidden, highlighted, selected, sticky }, classes) => {
    var _a, _b, _c, _d, _e, _f;
    const palette = theme.palette(color).table;
    return {
        actions: {
            position: "sticky",
            right: 0,
        },
        checkbox: Object.assign(Object.assign({}, (sticky && {
            position: "sticky",
            left: 0,
        })), { width: theme.size(16), minWidth: "min-content" }),
        row: {
            "backgroundColor": highlighted ? palette.row.highlighted.backgroundColor : selected ? palette.row.selected.backgroundColor : palette.row.backgroundColor,
            "visibility": hidden ? "collapse" : "visible",
            "thead &": {
                backgroundColor: palette.row.thead.backgroundColor,
                color: color !== "transparent" ? palette.row.thead.color : undefined,
            },
            "&:first-of-type": Object.assign(Object.assign({ "borderTop": ((_a = borders.horizontal) === null || _a === void 0 ? void 0 : _a.body) === "all" ? `${theme.size(1)} solid ${palette.row.borderColor}` : undefined }, (((_b = borders.horizontal) === null || _b === void 0 ? void 0 : _b.head) &&
                ["bottom", "none"].includes(borders.horizontal.head) && {
                "thead &": {
                    borderTop: 0,
                },
            })), { "thead + tbody &": {
                    borderTop: 0,
                } }),
            "&:last-of-type": Object.assign({ borderBottom: ((_c = borders.horizontal) === null || _c === void 0 ? void 0 : _c.body) === "all" ? `${theme.size(1)} solid ${palette.row.borderColor}` : undefined }, (((_d = borders.horizontal) === null || _d === void 0 ? void 0 : _d.head) &&
                ["top", "none"].includes(borders.horizontal.head) && {
                "thead &": {
                    borderBottom: 0,
                },
            })),
            [`& + .${classes.row}`]: Object.assign({ borderTop: ((_e = borders.horizontal) === null || _e === void 0 ? void 0 : _e.body) && ["all", "between"].includes(borders.horizontal.body) ? `${theme.size(1)} solid ${palette.row.borderColor}` : undefined }, (((_f = borders.horizontal) === null || _f === void 0 ? void 0 : _f.head) !== "all" && {
                "thead &": {
                    borderTop: 0,
                },
            })),
            "&:hover, &:focus": {
                outline: 0,
            },
        },
    };
});
export const TableRowSelectedContext = React.createContext({ selected: false });
export const TableRow = React.forwardRef(function TableRow(_a, ref) {
    var { actions, children, className, color = "transparent", hidden = false, highlighted = false, indeterminate = false, selected = false, selectable: selectionEnabled = true, onSelect } = _a, props = __rest(_a, ["actions", "children", "className", "color", "hidden", "highlighted", "indeterminate", "selected", "selectable", "onSelect"]);
    const { borders, selectable, sticky } = useContext(TableContext);
    const { parentColor } = useContext(TableSectionContext);
    const fixedColor = color === "transparent" ? parentColor !== null && parentColor !== void 0 ? parentColor : color : color;
    const styleParameters = useMemo(() => ({ borders, color: fixedColor, hidden, highlighted, selected, sticky }), [borders, fixedColor, hidden, highlighted, selected, sticky]);
    const { classes, cx } = useStyles(styleParameters);
    return (_jsx("tr", Object.assign({ ref: ref, className: cx(classes.row, className) }, props, { children: _jsxs(TableRowSelectedContext.Provider, { value: { selected }, children: [selectable && (_jsx(TableCell, { className: classes.checkbox, color: color, children: _jsx(Checkbox, { color: getColorComponent(color === "transparent" ? parentColor : color), disabled: !selectionEnabled, indeterminate: indeterminate, value: selected, onChange: onSelect }) })), children, actions && (_jsx(TableCell, { align: "right", className: classes.actions, children: actions }))] }) })));
});
export default React.memo(TableRow);
//# sourceMappingURL=table-row.js.map